import { useState, useEffect, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";
import { reportAPI } from "../utils/api";
import PatientDetail from "./PatientDetail";

const TYPE_COLORS = {
  Hematology:  "#2563EB",
  Hormone:     "#7C3AED",
  Immunology:  "#0891B2",
  Biochemical: "#059669",
  Other:       "#64748B",
};

const STATUS_OPTIONS = ["All", "Normal", "Abnormal"];
const TYPE_OPTIONS   = ["All", "Hematology", "Hormone", "Immunology", "Biochemical", "Other"];
const DATE_OPTIONS   = ["All", "Today", "This Week", "This Month", "This Year"];
const PAGE_SIZE      = 20;

export default function LabReports() {
  const [search,       setSearch]       = useState("");
  const [typeFilter,   setTypeFilter]   = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter,   setDateFilter]   = useState("All");
  const [page,         setPage]         = useState(1);
  const [data,         setData]         = useState({ reports: [], total: 0, total_pages: 1, stats: {} });
  const [loading,      setLoading]      = useState(true);
  const [navigateTo,   setNavigateTo]   = useState(() => {
    const pid = sessionStorage.getItem("emr_selected_patient");
    const rid = sessionStorage.getItem("emr_pd_rep");
    if (pid && rid) return { patientId: pid, reportId: rid };
    return null;
  });

  const debounceRef = useRef(null);

  const loadReports = useCallback(async (pg = 1, srch = search, typ = typeFilter, stat = statusFilter, dt = dateFilter) => {
    setLoading(true);
    try {
      const result = await reportAPI.list({
        page:         pg,
        pageSize:     PAGE_SIZE,
        search:       srch,
        reportType:   typ !== "All" ? typ : "",
        dateFilter:   dt !== "All" ? dt : "",
        statusFilter: stat !== "All" ? stat : "",
      });
      setData(result);
      setPage(pg);
    } catch (e) {
      console.error("Failed to load reports:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports(1, search, typeFilter, statusFilter, dateFilter);
  }, [typeFilter, statusFilter, dateFilter]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadReports(1, val, typeFilter, statusFilter, dateFilter);
    }, 400);
  };

  const handlePageChange = (pg) => {
    loadReports(pg, search, typeFilter, statusFilter, dateFilter);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (navigateTo) {
    return (
      <PatientDetail
        patientId={navigateTo.patientId}
        initialRepId={navigateTo.reportId}
        onBack={() => {
          ["emr_pd_view","emr_pd_type","emr_pd_rep","emr_pd_param","emr_selected_patient"].forEach(k => sessionStorage.removeItem(k));
          setNavigateTo(null);
          loadReports(page, search, typeFilter, statusFilter, dateFilter);
        }}
      />
    );
  }

  const filterBtnStyle = (active) => ({
    padding: "5px 12px", borderRadius: 99, fontSize: 12, cursor: "pointer",
    border: "1px solid", fontWeight: active ? 700 : 400,
    background: active ? "#0F172A" : "#fff",
    borderColor: active ? "#0F172A" : "#E4E8F0",
    color: active ? "#fff" : "#475569",
    transition: "all 0.15s",
  });

  const stats    = data.stats || {};
  const reports  = data.reports || [];
  const total    = data.total || 0;
  const totalPages = data.total_pages || 1;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: 0 }}>Lab Register</h2>
        <p style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>All laboratory reports across all patients</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        {[
          { label: "Total Reports", value: stats.total || total, color: "#2563EB" },
          { label: "Abnormal",      value: stats.has_abnormal || 0, color: "#DC2626" },
          { label: "Normal",        value: (stats.total || total) - (stats.has_abnormal || 0), color: "#16A34A" },
          { label: "Test Types",    value: stats.test_types || 0, color: "#7C3AED" },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: "14px 18px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: "14px 20px", marginBottom: 12 }}>
        <div className="search-wrap" style={{ marginBottom: 12 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input className="input" value={search} onChange={e => handleSearch(e.target.value)}
            placeholder="Search by patient name, EMR ID, lab ID, test type..." />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase" }}>Date:</span>
          {DATE_OPTIONS.map(d => (
            <button key={d} style={filterBtnStyle(dateFilter === d)} onClick={() => setDateFilter(d)}>{d}</button>
          ))}
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", marginLeft: 8 }}>Type:</span>
          {TYPE_OPTIONS.map(t => (
            <button key={t} style={{
              ...filterBtnStyle(typeFilter === t),
              borderColor: typeFilter === t ? (TYPE_COLORS[t] || "#0F172A") : "#E4E8F0",
              background:  typeFilter === t ? (TYPE_COLORS[t] || "#0F172A") : "#fff",
              color:       typeFilter === t ? "#fff" : "#475569",
            }} onClick={() => setTypeFilter(t)}>{t}</button>
          ))}
          <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", marginLeft: 8 }}>Status:</span>
          {STATUS_OPTIONS.map(s => (
            <button key={s} style={filterBtnStyle(statusFilter === s)} onClick={() => setStatusFilter(s)}>{s}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="empty-state"><p>Loading reports...</p></div>
        ) : reports.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <h3>No reports found</h3>
            <p>Try adjusting your filters or search term.</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 120px 90px 150px 90px 60px", padding: "10px 20px", background: "#F8F9FC", borderBottom: "2px solid #E4E8F0" }}>
              {["Lab ID", "Patient", "Type", "Date", "Lab", "Result", "Action"].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</div>
              ))}
            </div>

            {reports.map((r, idx) => (
              <div
                key={r.report_id}
                style={{
                  display: "grid", gridTemplateColumns: "80px 1fr 120px 90px 150px 90px 60px",
                  padding: "12px 20px", borderBottom: "1px solid #F1F5F9",
                  background: idx % 2 === 0 ? "#fff" : "#FAFBFC",
                  alignItems: "center", cursor: "pointer",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#EFF6FF"}
                onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#FAFBFC"}
                onClick={() => {
                  sessionStorage.removeItem("emr_pd_param");
                  sessionStorage.setItem("emr_active_page", "reports");
                  sessionStorage.setItem("emr_selected_patient", r.patient_id);
                  sessionStorage.setItem("emr_pd_rep", r.report_id);
                  sessionStorage.setItem("emr_pd_view", "report");
                  sessionStorage.setItem("emr_pd_type", r.type);
                  setNavigateTo({ patientId: r.patient_id, reportId: r.report_id });
                }}
              >
                <div>
                  {r.lab_id
                    ? <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: "#0F172A" }}>#{r.lab_id}</span>
                    : <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#EFF6FF", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                    {r.patient_name?.charAt(0)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{r.patient_name}</div>
                    <div style={{ fontSize: 11, color: "#94A3B8" }}>{r.patient_id} · {r.patient_age}y {r.patient_sex}</div>
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: (TYPE_COLORS[r.type] || "#64748B") + "15", color: TYPE_COLORS[r.type] || "#64748B" }}>
                    {r.type || "—"}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>{r.date}</div>
                <div style={{ fontSize: 12, color: "var(--slate-500)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.lab || "—"}</div>
                <div>
                  {r.abnormal_count > 0
                    ? <span style={{ fontSize: 12, color: "var(--red)", fontWeight: 600 }}>Abnormal</span>
                    : <span style={{ fontSize: 12, color: "var(--green)", fontWeight: 600 }}>Normal</span>}
                </div>
                <div>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={e => {
                      e.stopPropagation();
                      sessionStorage.removeItem("emr_pd_param");
                      sessionStorage.setItem("emr_active_page", "reports");
                      sessionStorage.setItem("emr_selected_patient", r.patient_id);
                      sessionStorage.setItem("emr_pd_rep", r.report_id);
                      sessionStorage.setItem("emr_pd_view", "report");
                      sessionStorage.setItem("emr_pd_type", r.type);
                      setNavigateTo({ patientId: r.patient_id, reportId: r.report_id });
                    }}
                    style={{ fontSize: 11 }}
                  >
                    View →
                  </button>
                </div>
              </div>
            ))}

            {/* Pagination footer */}
            <div style={{ padding: "12px 20px", borderTop: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#F8F9FC" }}>
              <div style={{ fontSize: 12, color: "#94A3B8" }}>
                Showing {((page-1)*PAGE_SIZE)+1}–{Math.min(page*PAGE_SIZE, total)} of {total} reports
              </div>
              {totalPages > 1 && (
                <div style={{ display: "flex", gap: 4 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => handlePageChange(page-1)} disabled={page<=1}>← Prev</button>
                  {Array.from({length: totalPages}, (_,i)=>i+1)
                    .filter(p => p===1 || p===totalPages || Math.abs(p-page)<=1)
                    .reduce((acc,p,idx,arr) => { if(idx>0 && p-arr[idx-1]>1) acc.push("..."); acc.push(p); return acc; }, [])
                    .map((p,i) => p==="..." ? (
                      <span key={i} style={{padding:"4px 8px",fontSize:12,color:"#94A3B8"}}>...</span>
                    ) : (
                      <button key={i} className={`btn btn-sm ${p===page?"btn-primary":"btn-ghost"}`} onClick={()=>handlePageChange(p)}>{p}</button>
                    ))
                  }
                  <button className="btn btn-ghost btn-sm" onClick={() => handlePageChange(page+1)} disabled={page>=totalPages}>Next →</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}